import { useState, useEffect } from 'react'
import { X, Download, Smartphone, Share2 } from 'lucide-react'
import { storage } from '@/store/storage'

export function PwaInstallBanner() {
  const [visible, setVisible] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isIOS, setIsIOS] = useState(false)
  const [isStandalone, setIsStandalone] = useState(false)

  useEffect(() => {
    // 检测平台
    const userAgent = navigator.userAgent.toLowerCase()
    const ios = /iphone|ipad|ipod/.test(userAgent)
    const standalone = window.matchMedia('(display-mode: standalone)').matches
    
    setIsIOS(ios)
    setIsStandalone(standalone)
    
    // 只有在未关闭过横幅且不是独立模式时才显示
    const shouldShow = !storage.getDismissedPwaBanner() && !standalone
    setVisible(shouldShow)
    
    // 监听beforeinstallprompt事件（Android/Chrome）
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      console.log('PWA: beforeinstallprompt event fired')
    }
    
    // 如果已经可以安装，也显示横幅
    if ((window as any).deferredPrompt) {
      setDeferredPrompt((window as any).deferredPrompt)
    }
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    }
  }, [])

  if (!visible) return null

  const handleInstall = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt()
        const choiceResult = await deferredPrompt.userChoice
        if (choiceResult.outcome === 'accepted') {
          console.log('PWA: User accepted install')
        } else {
          console.log('PWA: User dismissed install')
        }
        setDeferredPrompt(null)
      } catch (error) {
        console.error('PWA: Install error', error)
      }
    }
    dismiss()
  }

  const dismiss = () => {
    setVisible(false)
    storage.setDismissedPwaBanner(true)
  }

  return (
    <div className="fixed bottom-16 left-0 right-0 z-30 p-4 safe-bottom animate-slide-in">
      <div className="max-w-lg mx-auto bg-gradient-to-r from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 rounded-2xl p-4 border border-primary/20 shadow-lg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            {isIOS ? <Share2 className="h-5 w-5 text-primary" /> : <Download className="h-5 w-5 text-primary" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">添加到主屏幕</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {isIOS ? '点击分享按钮，选择"添加到主屏幕"' : '获得类原生App体验，支持后台提醒'}
            </p>
          </div>
          <button onClick={dismiss} className="p-1 hover:bg-muted rounded-lg transition-colors">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
        
        {isIOS ? (
          <div className="mt-3 text-xs text-muted-foreground bg-white/50 dark:bg-black/30 p-2 rounded-lg">
            <p className="font-medium">iOS用户指南：</p>
            <ol className="list-decimal pl-4 mt-1 space-y-1">
              <li>点击Safari底部分享按钮 <Share2 className="inline h-3 w-3" /></li>
              <li>向下滚动找到"添加到主屏幕"</li>
              <li>点击"添加"，然后从桌面启动</li>
            </ol>
          </div>
        ) : (
          <button
            onClick={handleInstall}
            className="mt-3 w-full py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:opacity-90 transition-opacity shadow-md"
            disabled={!deferredPrompt}
          >
            {deferredPrompt ? '立即添加' : '等待安装提示...'}
          </button>
        )}
        
        <div className="mt-2 text-[10px] text-muted-foreground opacity-70">
          {isIOS ? 'iOS需要手动添加' : '支持Chrome/Edge/Android浏览器'}
        </div>
      </div>
    </div>
  )
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}
